import { NetworkStatus, OperationVariables } from "@apollo/client";
import type { useQuery } from "@apollo/client/react";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { PageInfoFragmentFragment } from "@repo/graphql/documents/generic/fragment.generated";
import { AlertCircleIcon, X } from "lucide-react-native";
import { useCallback, useState } from "react";
import { Controller, FieldValues } from "react-hook-form";
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
} from "react-native";
import Search from "../../shared/search";
import { Badge, BadgeText } from "../../ui/badge";
import { Box } from "../../ui/box";
import { Button, ButtonIcon, ButtonText } from "../../ui/button";
import { HStack } from "../../ui/hstack";
import { Popover, PopoverBackdrop, PopoverContent } from "../../ui/popover";
import { Text } from "../../ui/text";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputSelectProps } from "../types";

type Props<
  TFieldValues extends FieldValues,
  TData,
  TVariables extends { after?: string | null } & OperationVariables,
> = FormInputSelectProps<TFieldValues, string[]> & {
  networkStatus?: NetworkStatus;
  emptyOptionsMessage?: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  getOptionLabel: (value: string) => string | undefined;
  pageInfo?: PageInfoFragmentFragment;
  fetchMore?: useQuery.Result<TData, TVariables>["fetchMore"];
};

export default function FormInputMultipleOptionsSearch<
  TFieldValues extends FieldValues,
  TData,
  TVariables extends { after?: string | null } & OperationVariables,
>(props: Props<TFieldValues, TData, TVariables>) {
  const {
    control,
    name,
    label,
    required,
    disabled,
    className,
    size,
    options,
    labelSize,
    placeholder,
    networkStatus,
    emptyOptionsMessage,
    setSearch,
    getOptionLabel,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (!props.pageInfo?.hasNextPage || networkStatus !== NetworkStatus.ready)
        return;
      const { contentOffset, contentSize, layoutMeasurement } =
        event.nativeEvent;
      if (
        contentOffset.y + layoutMeasurement.height >=
        contentSize.height - 50
      ) {
        props.fetchMore?.({
          variables: {
            after: props.pageInfo?.endCursor,
          } as Partial<TVariables>,
        });
      }
    },
    [networkStatus, props],
  );
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value, ref }, fieldState: { error } }) => {
        const filteredOptions = options.filter(
          (option) => !value?.includes(option.value),
        );

        return (
          <FormControl
            isInvalid={!!error}
            isRequired={required}
            isDisabled={disabled}
            className={className}
            size={size}
            ref={focusScrollRef(ref)}
          >
            <FormControlLabel>
              <FormControlLabelText size={labelSize || size}>
                {label}
              </FormControlLabelText>
            </FormControlLabel>
            {value.length > 0 && (
              <Box className="mt-1">
                <HStack flexSpace="sm">
                  {value.map((option: string) => {
                    return (
                      <Box key={option}>
                        <Badge
                          action="muted"
                          variant="outline"
                          size="md"
                          key={option}
                        >
                          <BadgeText>{getOptionLabel(option)}</BadgeText>
                          <Button
                            variant="link"
                            size="none"
                            className="ml-1"
                            onPress={() => {
                              onChange(
                                value.filter((v: string) => v !== option),
                              );
                              setIsOpen(false);
                            }}
                          >
                            <ButtonIcon size="sm" as={X} />
                          </Button>
                        </Badge>
                      </Box>
                    );
                  })}
                </HStack>
              </Box>
            )}
            <Popover
              placement="bottom left"
              offset={5}
              isOpen={isOpen}
              trapFocus={false}
              focusScope={false}
              size="lg"
              onClose={() => setIsOpen(false)}
              trigger={({ ...triggerProps }) => {
                return (
                  <Box className="my-1 pr-2">
                    <Search
                      ref={triggerProps.ref}
                      placeholder={placeholder}
                      onChange={setSearch}
                      loading={
                        networkStatus &&
                        [
                          NetworkStatus.loading,
                          NetworkStatus.refetch,
                          NetworkStatus.setVariables,
                        ].includes(networkStatus)
                      }
                      size={size}
                      inputFieldProps={{
                        onFocus: () => {
                          setIsOpen(true);
                          setTimeout(() => {
                            triggerProps.ref.current?.focus();
                          }, 50);
                        },
                        autoFocus: isOpen,
                      }}
                    />
                  </Box>
                );
              }}
            >
              <PopoverBackdrop />
              <PopoverContent className="p-1 min-w-[400px] max-h-[400px]">
                <ScrollView
                  onScroll={onScroll}
                  className="overflow-auto"
                  showsVerticalScrollIndicator={false}
                  showsHorizontalScrollIndicator={false}
                  scrollEventThrottle={16}
                >
                  {filteredOptions.map((content) => {
                    return (
                      <Button
                        className="rounded-lg justify-start"
                        variant="solid"
                        action="default"
                        size="sm"
                        onPress={() => {
                          onChange([...(value || []), content.value]);
                          setSearch("");
                        }}
                        key={`${content.value}-item`}
                      >
                        <ButtonText size={size}>{content.label}</ButtonText>
                      </Button>
                    );
                  })}
                  {!filteredOptions.length &&
                    emptyOptionsMessage &&
                    networkStatus === NetworkStatus.ready && (
                      <Box className="px-4 py-4">
                        <Text action="muted" size="sm">
                          {networkStatus === NetworkStatus.ready
                            ? emptyOptionsMessage
                            : networkStatus === NetworkStatus.setVariables
                              ? "Buscando..."
                              : ""}
                        </Text>
                      </Box>
                    )}
                </ScrollView>
              </PopoverContent>
            </Popover>
            <FormControlError>
              <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
              <FormControlErrorText>{error?.message}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
